import { getDistance } from 'geolib';

/** Max distance (m) between line endpoints to treat KML geometry as the same route. */
export const ROTA_ENDPOINT_MATCH_METERS = 25;

export function parseRotaLineCoords(rota) {
  if (!rota) return null;
  if (rota.geojson != null && rota.geojson !== '') {
    let gj = rota.geojson;
    if (typeof gj === 'string') {
      try {
        gj = JSON.parse(gj);
      } catch (_) {
        gj = null;
      }
    }
    if (gj) {
      const geom = gj.geometry || (gj.type === 'LineString' ? gj : null);
      if (geom && geom.type === 'LineString' && Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
        return geom.coordinates;
      }
      if (gj.type === 'Feature' && gj.geometry && gj.geometry.type === 'LineString') {
        const coords = gj.geometry.coordinates;
        if (Array.isArray(coords) && coords.length >= 2) return coords;
      }
    }
  }
  if (rota.shape != null && rota.shape !== '') {
    let shape = rota.shape;
    if (typeof shape === 'string') {
      try {
        shape = JSON.parse(shape);
      } catch (_) {
        return null;
      }
    }
    if (Array.isArray(shape) && shape.length >= 2) {
      return shape.map((p) => [Number(p.lg), Number(p.lt)]);
    }
  }
  return null;
}

export function endpointsFromCoords(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const [lng0, lat0] = coords[0];
  const [lngN, latN] = coords[coords.length - 1];
  const startLat = Number(lat0);
  const startLng = Number(lng0);
  const endLat = Number(latN);
  const endLng = Number(lngN);
  if (![startLat, startLng, endLat, endLng].every((n) => isFinite(n))) return null;
  return {
    start: { latitude: startLat, longitude: startLng },
    end: { latitude: endLat, longitude: endLng },
  };
}

/**
 * Normaliza catálogo (payload leve do match ou rotas completas) para objetos
 * com extremos já resolvidos — evita JSON.parse repetido no match.
 */
export function slimRotasCatalogForMatch(catalog) {
  if (!Array.isArray(catalog) || !catalog.length) return [];
  const out = [];
  for (let i = 0; i < catalog.length; i++) {
    const r = catalog[i];
    if (r == null || r.id_rota == null) continue;
    let ends = null;
    if (
      r.start_lat != null &&
      r.start_lng != null &&
      r.end_lat != null &&
      r.end_lng != null &&
      isFinite(Number(r.start_lat)) &&
      isFinite(Number(r.start_lng)) &&
      isFinite(Number(r.end_lat)) &&
      isFinite(Number(r.end_lng))
    ) {
      ends = {
        start: { latitude: Number(r.start_lat), longitude: Number(r.start_lng) },
        end: { latitude: Number(r.end_lat), longitude: Number(r.end_lng) },
      };
    } else {
      ends = endpointsFromCoords(parseRotaLineCoords(r));
    }
    out.push({
      id_rota: r.id_rota,
      cod_rota: r.cod_rota,
      nome_rota: r.nome_rota,
      id_empresa: r.id_empresa,
      _ends: ends,
    });
  }
  return out;
}

export function findExistingRotaForFeature(feature, catalog) {
  if (!feature || !feature.geometry || feature.geometry.type !== 'LineString') {
    return null;
  }
  const kmlEnds = endpointsFromCoords(feature.geometry.coordinates);
  if (!kmlEnds || !Array.isArray(catalog) || catalog.length === 0) {
    return null;
  }
  let best = null;
  let bestScore = Infinity;
  for (let i = 0; i < catalog.length; i++) {
    const r = catalog[i];
    if (r == null || r.id_rota == null) continue;
    const ends =
      r._ends ||
      (r.start_lat != null
        ? {
            start: { latitude: Number(r.start_lat), longitude: Number(r.start_lng) },
            end: { latitude: Number(r.end_lat), longitude: Number(r.end_lng) },
          }
        : endpointsFromCoords(parseRotaLineCoords(r)));
    if (!ends) continue;
    const dSS = getDistance(kmlEnds.start, ends.start);
    const dEE = getDistance(kmlEnds.end, ends.end);
    const dSE = getDistance(kmlEnds.start, ends.end);
    const dES = getDistance(kmlEnds.end, ends.start);
    const fwdOk = dSS <= ROTA_ENDPOINT_MATCH_METERS && dEE <= ROTA_ENDPOINT_MATCH_METERS;
    const revOk = dSE <= ROTA_ENDPOINT_MATCH_METERS && dES <= ROTA_ENDPOINT_MATCH_METERS;
    if (!fwdOk && !revOk) continue;
    const score = Math.min(fwdOk ? dSS + dEE : Infinity, revOk ? dSE + dES : Infinity);
    if (score < bestScore) {
      bestScore = score;
      best = r;
    }
  }
  return best;
}

/** Yields to the browser so Vue can paint (avoids long main-thread stalls). */
export function yieldToMain() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        setTimeout(resolve, 0);
      });
    } else {
      setTimeout(resolve, 0);
    }
  });
}
